// ============================================================
// Project Workspace · operator shells — /square/projects/:id/<surface>
//
// Phase 1 = SHELLS ONLY. Each surface renders the shared ProjectSurface
// chrome + a polished empty state describing what will live there and in
// which phase. No messages/files/tasks/calendar/deliverables/approvals
// backend is implemented here — those are explicitly later phases.
//
// One parameterized component (WorkspaceShell surface="...") backs all
// ten routes so the router stays declarative and the copy lives in one
// SURFACES map. The `workspace` surface is the hub: it also renders a nav
// grid into the sibling surfaces.
//
// Operator-only: these routes live under /square/* which is wrapped by
// AccessGate (GitHub auth + allowlist). This file does not weaken that.
// ============================================================

import { useNavigate, useParams } from 'react-router-dom'
import ProjectSurface from './ProjectSurface'
import { SHELL_TOKENS, chipStyle, text } from '../../shell/styles'

export type WorkspaceSurfaceKey =
  | 'workspace'
  | 'messages'
  | 'files'
  | 'deliverables'
  | 'tasks'
  | 'calendar'
  | 'testing'
  | 'approvals'
  | 'activity'
  | 'client-access'

interface SurfaceDef {
  path: string
  kicker: string
  title: string
  glyph: string
  lead: string
  body: string
  phase: string
}

const SURFACES: Record<WorkspaceSurfaceKey, SurfaceDef> = {
  workspace: {
    path: 'workspace',
    kicker: 'PROJECT · WORKSPACE',
    title: 'Workspace',
    glyph: '◧',
    lead: 'The project rollup and current work state.',
    body: 'This is the operator hub for the project. Once the workspace surfaces are live, the project rollup — open work, recent activity, and next actions — lands here. Pick a surface below to preview where it will live.',
    phase: 'Phase 1 · shell',
  },
  messages: {
    path: 'messages',
    kicker: 'PROJECT · MESSAGES',
    title: 'Messages',
    glyph: '✉',
    lead: 'Persistent project messages.',
    body: 'Comms today is local-state only and not persistent. Persistent, project-scoped messages (operator ⇄ client, RLS-scoped) land here in Phase 2.',
    phase: 'Phase 2',
  },
  files: {
    path: 'files',
    kicker: 'PROJECT · FILES',
    title: 'Files',
    glyph: '▢',
    lead: 'Project-owned files.',
    body: 'Project-owned files with signed access and storage-backed uploads land here in Phase 3. Nothing is uploaded or stored yet.',
    phase: 'Phase 3',
  },
  deliverables: {
    path: 'deliverables',
    kicker: 'PROJECT · DELIVERABLES',
    title: 'Deliverables',
    glyph: '◼',
    lead: 'Studio / QuamSpace outputs.',
    body: 'Finished Studio / QuamSpace outputs are promoted to client-visible deliverables here in Phase 3/6. No deliverable posting is implemented yet.',
    phase: 'Phase 3/6',
  },
  tasks: {
    path: 'tasks',
    kicker: 'PROJECT · TASKS',
    title: 'Tasks',
    glyph: '☑',
    lead: 'Tasks and milestones.',
    body: 'Tasks and milestones — assignable, status-tracked, client-visible where appropriate — land here in Phase 4. No task model is implemented yet.',
    phase: 'Phase 4',
  },
  calendar: {
    path: 'calendar',
    kicker: 'PROJECT · CALENDAR',
    title: 'Calendar',
    glyph: '◷',
    lead: 'Quam Calendar.',
    body: 'The Quam Calendar — sessions, dates, and scheduling — lands here in Phase 5. No calendar or external sync is implemented yet.',
    phase: 'Phase 5',
  },
  testing: {
    path: 'testing',
    kicker: 'PROJECT · TESTING',
    title: 'Testing',
    glyph: '◇',
    lead: 'Demo and staging links.',
    body: 'Demo and staging links for client review land here in Phase 7. No preview links are wired yet.',
    phase: 'Phase 7',
  },
  approvals: {
    path: 'approvals',
    kicker: 'PROJECT · APPROVALS',
    title: 'Approvals',
    glyph: '◎',
    lead: 'Approval flow.',
    body: 'The client approval flow — request, decision, and receipt — lands here in Phase 7. No approval flow is implemented yet.',
    phase: 'Phase 7',
  },
  activity: {
    path: 'activity',
    kicker: 'PROJECT · ACTIVITY',
    title: 'Activity',
    glyph: '◔',
    lead: 'Receipts and activity.',
    body: 'A project activity stream — receipts of what happened and when — lands here in a later phase. Nothing is recorded to it yet.',
    phase: 'Later',
  },
  'client-access': {
    path: 'client-access',
    kicker: 'PROJECT · CLIENT ACCESS',
    title: 'Client Access',
    glyph: '◈',
    lead: 'Members, invites, and access.',
    body: 'Operator management of project members and access invites, with role and status labels. Reads are live; write actions are placeholders until membership is activated. Client Connect stays fail-closed until then.',
    phase: 'Phase 1B',
  },
}

// Hub nav order (excludes the hub itself).
const NAV_ORDER: WorkspaceSurfaceKey[] = [
  'messages',
  'files',
  'deliverables',
  'tasks',
  'calendar',
  'testing',
  'approvals',
  'activity',
  'client-access',
]

export default function WorkspaceShell({ surface }: { surface: WorkspaceSurfaceKey }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const def = SURFACES[surface]

  return (
    <ProjectSurface kicker={def.kicker} title={def.title} description={def.lead}>
      {surface === 'workspace' && (
        <section style={{ marginBottom: 24 }}>
          <div
            style={{
              ...text.kicker,
              fontSize: 9,
              letterSpacing: '0.24em',
              color: SHELL_TOKENS.inkWhisper,
              marginBottom: 10,
            }}
          >
            WORKSPACE SURFACES
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 10,
            }}
          >
            {NAV_ORDER.map((key) => {
              const s = SURFACES[key]
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => id && navigate(`/square/projects/${id}/${s.path}`)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    padding: '14px 14px',
                    borderRadius: 12,
                    border: `1px solid ${SHELL_TOKENS.border}`,
                    background: SHELL_TOKENS.surface,
                    color: SHELL_TOKENS.ink,
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = SHELL_TOKENS.brandLine
                    e.currentTarget.style.background = SHELL_TOKENS.brandSoft
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = SHELL_TOKENS.border
                    e.currentTarget.style.background = SHELL_TOKENS.surface
                  }}
                >
                  <span style={{ fontSize: 16, color: SHELL_TOKENS.brand, fontFamily: 'var(--font-brand)' }}>
                    {s.glyph}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{s.title}</span>
                  <span style={chipStyle(SHELL_TOKENS.inkFaint)}>{s.phase}</span>
                </button>
              )
            })}
          </div>
        </section>
      )}

      <WorkspaceEmptyState glyph={def.glyph} title={def.title} body={def.body} phase={def.phase} />
    </ProjectSurface>
  )
}

function WorkspaceEmptyState({
  glyph,
  title,
  body,
  phase,
}: {
  glyph: string
  title: string
  body: string
  phase: string
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: '44px 28px',
        borderRadius: 16,
        border: `1px dashed ${SHELL_TOKENS.borderAlt}`,
        background: SHELL_TOKENS.surface,
        gap: 12,
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: SHELL_TOKENS.brandSoft,
          border: `1px solid ${SHELL_TOKENS.brandLine}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          color: SHELL_TOKENS.brand,
          fontFamily: 'var(--font-brand)',
        }}
      >
        {glyph}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: SHELL_TOKENS.ink }}>{title}</div>
      <div style={{ maxWidth: 460, fontSize: 13, lineHeight: 1.6, color: SHELL_TOKENS.inkMuted }}>{body}</div>
      <span style={chipStyle(SHELL_TOKENS.blue)}>{phase}</span>
    </div>
  )
}
